import React, { FC, useRef, useEffect, SyntheticEvent, useState, KeyboardEvent } from 'react';
import { isIE, isFirefox } from '../util';
import { isEmpty, map } from 'lodash';
import { DROP_DOWN_HEIGHTS } from '../constants';

const LIST_ITEM_ROLE = isIE() || isFirefox() ? 'list' : 'listbox';
const LIST_ROLE = isIE() || isFirefox() ? 'listitem' : 'option';

export interface DropdownItemProps {
  isDisabled?: boolean;
  label: string;
  value: string;
}
export interface DropdownListProps {
  options: DropdownItemProps[];
  selectedIndex?: number;
  placeholder?: string;
  isAutoSuggest?: boolean;
  required?: boolean;
  role?: string;
  timeout?: number;
}
const DropdownItem: FC<
  DropdownItemProps & {
    id: string;
    isSelected?: boolean;
    isBrowsed?: boolean;
    onClick: (e: SyntheticEvent, idx?: number) => void;
    idx: number;
  }
> = ({ isDisabled = false, id, label, isSelected, onClick, idx, isBrowsed }) => {
  return (
    <li
      id={id}
      aria-selected={isSelected}
      role="option"
      aria-disabled={!!isDisabled}
      className={`${isDisabled ? 'disable' : ''}${isSelected ? ' active' : ''}`}
      onClick={e => {
        e.preventDefault();
        onClick(e, idx);
      }}
      data-testid={id}
      aria-live={isBrowsed ? 'polite' : 'off'}
      aria-atomic={true}>
      {label}
    </li>
  );
};
DropdownItem.displayName = 'DropdownItem';

const handleScroll = (listboxRef: React.RefObject<HTMLElement>, placeholder: string, browsedIndex: number): void => {
  const { scrollHeight, clientHeight } = listboxRef.current;
  if (scrollHeight > clientHeight) {
    const listIndex = !isEmpty(placeholder) && browsedIndex > 0 ? browsedIndex - 1 : browsedIndex;
    const elementBottom = (listIndex + DROP_DOWN_HEIGHTS.LIST_INDEX) * DROP_DOWN_HEIGHTS.LIST;

    if (elementBottom + DROP_DOWN_HEIGHTS.LIST > DROP_DOWN_HEIGHTS.LIST_BOX_MAX) {
      listboxRef.current.scrollTop = elementBottom - DROP_DOWN_HEIGHTS.LIST_BOX_MAX;
    } else if (listIndex !== -1 && listboxRef.current.scrollTop > listIndex * DROP_DOWN_HEIGHTS.LIST) {
      listboxRef.current.scrollTop = listIndex * DROP_DOWN_HEIGHTS.LIST;
    }
  }
};

export const DropdownList: FC<
  DropdownListProps & {
    id: string;
    ariaLabelledby: string;
    hasError?: boolean;
    onBlur: (e: SyntheticEvent) => void;
    onKeyDown: (e: KeyboardEvent) => void;
    onChange: (e: SyntheticEvent, idx?: number) => void;
    browsedIndex: number;
    setBrowsedIndex: (index: number) => void;
  }
> = ({
  options,
  selectedIndex,
  onChange,
  id,
  ariaLabelledby,
  onBlur,
  onKeyDown,
  placeholder,
  isAutoSuggest,
  hasError,
  required,
  browsedIndex,
  setBrowsedIndex
}) => {
  const listboxRef = useRef<HTMLUListElement>(null);

  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (!isAutoSuggest) {
      listboxRef.current.focus();
    }
  }, []);

  useEffect(() => {
    handleScroll(listboxRef, placeholder, browsedIndex);
  }, [placeholder, browsedIndex]);

  const handleMouseIn = () => {
    if (browsedIndex !== -1) {
      setBrowsedIndex(-1);
    }
    if (!isHovered) {
      setIsHovered(true);
    }
  };

  const handleMouseOut = () => {
    if (browsedIndex !== -1) {
      setBrowsedIndex(-1);
    }
    if (isHovered) {
      setIsHovered(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        const newIndex = browsedIndex + 1;
        setBrowsedIndex(Math.min(newIndex, options.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        const newwIndex = browsedIndex - 1;
        setBrowsedIndex(Math.max(newwIndex, 0));
        break;
      case 'Enter':
        e.preventDefault();
        onChange(e, browsedIndex);
        break;
      default:
        onKeyDown(e);
    }
  };

  return (
    // eslint-disable-next-line jsx-a11y/aria-activedescendant-has-tabindex
    <ul
      id={id}
      tabIndex={0}
      onBlur={onBlur}
      onKeyDown={handleKeyDown}
      aria-labelledby={ariaLabelledby}
      ref={listboxRef}
      onMouseEnter={handleMouseIn}
      onMouseMove={handleMouseIn}
      onMouseLeave={handleMouseOut}
      data-testid="drop-list"
      className={`drop-list${isHovered ? ' hovered' : ''}`}
      role="listbox"
      aria-invalid={hasError}
      aria-required={required}>
      {map(options, (option, idx) =>
        placeholder && idx === 0 ? null : (
          <DropdownItem
            id={`${id}-${option.value}`}
            onClick={onChange}
            isSelected={idx === selectedIndex}
            isBrowsed={idx === browsedIndex}
            key={`list-items-${id}-${idx}`}
            aria-describedby={`${id}-described`}
            aria-label="DropdownCountries"
            aria-haspopup="listbox"
            aria-expanded="true"
            idx={idx}
            {...option}
          />
        )
      )}
    </ul>
  );
};
DropdownList.displayName = 'DropdownList';
